/**
 * 검색 (MASTER §15). 한국어에 맞춘 단순·예측 가능한 방식:
 * - 띄어쓰기·문장부호 무시, 단어 일부도 찾음 (조사가 붙어도: "불면" → "불면이", "불면을")
 * - 여러 단어는 모두 들어 있는 것만 (AND)
 * - 정확히 맞는 결과가 없으면 오타 허용(글자 두 개씩 겹침 비율)으로 가까운 것을 찾는다
 * 색인은 잠금 해제된 동안 메모리에만 있고 디스크에 남기지 않는다 (구현계획 §16).
 */
export type SearchKind =
  | 'transcript'
  | 'memo'
  | 'journal'
  | 'analysis'
  | 'evidence'
  | 'formulation'
  | 'goal'
  | 'diagnosis'
  | 'brief'
  | 'document'
  | 'client';

export const SEARCH_KIND_LABELS: Record<SearchKind, string> = {
  transcript: '전사',
  memo: '상담자 메모',
  journal: '상담기록지',
  analysis: '회기 분석',
  evidence: '근거',
  formulation: '사례개념화',
  goal: '상담 목표',
  diagnosis: '진단적 고려',
  brief: '다음 회기 계획',
  document: '문서',
  client: '내담자 정보',
};

export type SearchDoc = {
  id: string;
  kind: SearchKind;
  clientId: string;
  clientCode: string;
  sessionId?: string;
  sessionNumber?: number;
  /** 날짜 (YYYY-MM-DD) — 필터용 */
  date?: string;
  /** 결과에 보일 출처, 예: "S07 · 18:43 · 전사" */
  source: string;
  text: string;
  /** 누르면 갈 곳 */
  link: string;
};

export type SearchFilter = { clientId?: string; kinds?: SearchKind[]; from?: string; to?: string };
export type SearchHit = { doc: SearchDoc; score: number; snippet: string; fuzzy: boolean };

export const squash = (t: string) => t.normalize('NFC').toLowerCase().replace(/[\s.,!?·"'“”‘’()[\]{}:;~\-_/]+/g, '');

function editDistance(a: string, b: string, cap: number): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j]! < rowMin) rowMin = cur[j]!;
    }
    if (rowMin > cap) return cap + 1;
    prev = cur;
  }
  return prev[b.length]!;
}

/** 오타 허용 (글자 단위): 질의 단어와 본문의 비슷한 길이 구간 사이 편집 거리. 3~5글자는 1글자, 6글자 이상은 2글자까지 */
function fuzzyScore(term: string, text: string): number {
  const w = term.length;
  if (w < 3) return 0;
  const cap = w >= 6 ? 2 : 1;
  // 빠른 거르기: 질의 글자 중 (길이 - 허용 오타) 개 이상이 본문에 있어야 함
  let present = 0;
  for (const ch of new Set(term)) if (text.includes(ch)) present++;
  if (present < new Set(term).size - cap) return 0;
  // 비교 시작점: 질의 앞쪽 글자(오타 허용 수만큼)가 나오는 자리 근처만
  const starts = new Set<number>();
  for (let k = 0; k <= cap; k++) {
    for (let i = text.indexOf(term[k]!); i >= 0; i = text.indexOf(term[k]!, i + 1))
      for (let d = -cap; d <= cap; d++) if (i - k + d >= 0) starts.add(i - k + d);
  }
  let best = cap + 1;
  for (const i of starts) {
    for (let len = w - cap; len <= w + cap && best > 0; len++) {
      if (len < 1 || i + len > text.length) continue;
      const d = editDistance(term, text.slice(i, i + len), cap);
      if (d < best) best = d;
    }
    if (best === 0) break;
  }
  return best > cap ? 0 : 1 - best / w;
}

export function makeSnippet(text: string, terms: string[], width = 90): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  const lower = flat.toLowerCase();
  let at = -1;
  for (const t of terms) {
    at = lower.indexOf(t.toLowerCase());
    if (at >= 0) break;
  }
  if (at < 0) return flat.length > width ? `${flat.slice(0, width)}…` : flat;
  const start = Math.max(0, at - Math.floor(width / 3));
  const end = Math.min(flat.length, start + width);
  return `${start > 0 ? '…' : ''}${flat.slice(start, end)}${end < flat.length ? '…' : ''}`;
}

export class SearchIndex {
  private readonly docs: { doc: SearchDoc; flat: string }[];
  constructor(docs: SearchDoc[]) {
    this.docs = docs.filter((d) => d.text.trim()).map((doc) => ({ doc, flat: squash(doc.text) }));
  }
  get size() {
    return this.docs.length;
  }

  search(query: string, filter: SearchFilter = {}, limit = 100): SearchHit[] {
    const raw = query.trim().split(/\s+/).filter(Boolean);
    const terms = raw.map(squash).filter(Boolean);
    if (!terms.length) return [];
    const pool = this.docs.filter(
      ({ doc }) =>
        (!filter.clientId || doc.clientId === filter.clientId) &&
        (!filter.kinds?.length || filter.kinds.includes(doc.kind)) &&
        (!filter.from || (doc.date ?? '') >= filter.from) &&
        (!filter.to || (doc.date ?? '9999') <= filter.to),
    );
    const exact: SearchHit[] = [];
    for (const { doc, flat } of pool) {
      if (!terms.every((t) => flat.includes(t))) continue;
      let count = 0;
      for (const t of terms) count += flat.split(t).length - 1;
      exact.push({ doc, score: count + (doc.kind === 'transcript' ? 0 : 0.5), snippet: makeSnippet(doc.text, raw), fuzzy: false });
    }
    if (exact.length) return exact.sort(byScoreThenDate).slice(0, limit);
    // 오타 허용 (정확한 결과가 없을 때만)
    const fuzzy: SearchHit[] = [];
    for (const { doc, flat } of pool) {
      const scores = terms.map((t) => (flat.includes(t) ? 1 : fuzzyScore(t, flat)));
      const min = Math.min(...scores);
      if (min >= 0.6) fuzzy.push({ doc, score: scores.reduce((a, b) => a + b, 0), snippet: makeSnippet(doc.text, raw), fuzzy: true });
      if (fuzzy.length >= limit * 3) break; // 비슷한 결과는 충분히 모이면 멈춘다 (속도)
    }
    return fuzzy.sort(byScoreThenDate).slice(0, limit);
  }
}

function byScoreThenDate(a: SearchHit, b: SearchHit) {
  return b.score - a.score || (b.doc.date ?? '').localeCompare(a.doc.date ?? '');
}
